// =============================================================
// ViewToggle
// -------------------------------------------------------------
// Segmented switch that flips between two views (e.g. "Table"
// and "Analytics"). Designed to sit at the top of a page so the
// management user can choose their preferred layout without
// losing the existing table when they switch back.
//
// Persistence:
//   * The chosen view is stored in localStorage under
//     `viewToggle:<key>` so the user's preference survives
//     page reloads and browser restarts.
//   * When localStorage isn't writable (e.g. incognito quota
//     exceeded) we fall back to in-memory state, so the toggle
//     still works during the session.
//
// Usage:
//   <ViewToggle
//     storageKey="enrollment"
//     value={view}
//     onChange={setView}
//   />
// =============================================================

import React, { useEffect, useState } from 'react';

const PREFIX = 'viewToggle:';

const readStored = (key) => {
  try { return localStorage.getItem(PREFIX + key); }
  catch (_) { return null; }
};
const writeStored = (key, value) => {
  try { localStorage.setItem(PREFIX + key, value); }
  catch (_) { /* quota or private mode — fall back silently */ }
};

export function readViewPreference(key, fallback) {
  const v = readStored(key);
  return v || fallback;
}

export default function ViewToggle({
  storageKey,
  value,
  onChange,
  options = [
    { value: 'table',    label: 'Table View',    icon: 'bi-table' },
    { value: 'analytics', label: 'Analytics',     icon: 'bi-bar-chart-line-fill' },
  ],
  size = 'md',                // 'sm' | 'md' | 'lg'
  className = '',
}) {
  // Initialise from storage on mount. The parent can override
  // via `value` once it has read the same key on its end.
  const [internal, setInternal] = useState(() => value || readStored(storageKey) || options[0].value);
  const current = value !== undefined ? value : internal;

  useEffect(() => {
    if (storageKey) writeStored(storageKey, current);
  }, [storageKey, current]);

  const handle = (next) => {
    if (next === current) return;
    if (value === undefined) setInternal(next);
    if (storageKey) writeStored(storageKey, next);
    if (typeof onChange === 'function') onChange(next);
  };

  const pad = size === 'sm' ? '4px 10px' : size === 'lg' ? '8px 18px' : '6px 14px';
  const fs  = size === 'sm' ? 12 : size === 'lg' ? 15 : 13;
  const ih  = size === 'sm' ? 14 : size === 'lg' ? 18 : 16;

  return (
    <div
      role="tablist"
      aria-label="View toggle"
      className={className}
      style={{
        display: 'inline-flex',
        background: 'rgba(15, 23, 42, 0.06)',
        border: '1px solid rgba(15, 23, 42, 0.10)',
        borderRadius: 999,
        padding: 3,
        gap: 2,
      }}
    >
      {options.map(opt => {
        const active = opt.value === current;
        return (
          <button
            key={opt.value}
            role="tab"
            type="button"
            aria-selected={active}
            onClick={() => handle(opt.value)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: pad,
              fontSize: fs, fontWeight: 600,
              background: active ? '#ffffff' : 'transparent',
              color: active ? '#1e3a8a' : '#475569',
              border: 'none',
              borderRadius: 999,
              cursor: 'pointer',
              boxShadow: active
                ? '0 1px 3px rgba(15, 23, 42, 0.10), 0 1px 2px rgba(15, 23, 42, 0.06)'
                : 'none',
              transition: 'background .15s ease, color .15s ease, box-shadow .15s ease',
            }}
          >
            <i className={`bi ${opt.icon}`} style={{ fontSize: ih }} />
            <span>{opt.label}</span>
          </button>
        );
      })}
    </div>
  );
}
